import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Shield, Clock, Upload, Download, Info
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  fetchEmployeeTaxProfile, updateEmployeeTaxProfile, 
  fetchEmployeeTaxSummary, fetchTdsHistory 
} from '../../services/incomeTaxApi';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const EmployeeIncomeTax = () => {
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const [taxYear, setTaxYear] = useState('2026-27');
  const [activeSubTab, setActiveSubTab] = useState('summary');
  
  // Data state
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tdsHistory, setTdsHistory] = useState([]);
  
  // UI state
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [updatingRegime, setUpdatingRegime] = useState(false);

  const loadEmployeeTaxData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Load profile & summary on summary tab
      if (activeSubTab === 'summary') {
        try {
          const profileData = await fetchEmployeeTaxProfile(employeeId, taxYear);
          setProfile(profileData);
        } catch (err) {
          // If 404, we don't have a profile yet
          setProfile(null);
        }
        
        try {
          const summaryData = await fetchEmployeeTaxSummary(employeeId, taxYear);
          setSummary(summaryData);
        } catch (err) {
          setSummary(null);
          setError(err.message || 'No active configuration found for this financial year.');
        }
      } 
      // 2. Load worksheet calculation explanation
      else if (activeSubTab === 'calculation') {
        const summaryData = await fetchEmployeeTaxSummary(employeeId, taxYear);
        setSummary(summaryData);
      } 
      // 3. Load TDS history
      else if (activeSubTab === 'history') {
        const hist = await fetchTdsHistory(employeeId, taxYear);
        setTdsHistory(hist);
      }
    } catch (err) {
      setError(err.message || 'Error loading tax details.');
    } finally {
      setLoading(false);
    }
  }, [employeeId, taxYear, activeSubTab]);

  useEffect(() => {
    if (employeeId) {
      loadEmployeeTaxData();
    }
  }, [employeeId, loadEmployeeTaxData]);

  const handleRegimeChange = async (newRegime) => {
    if (!employeeId) return;
    setUpdatingRegime(true);
    setError('');
    setSuccess('');
    try {
      await updateEmployeeTaxProfile(employeeId, taxYear, newRegime);
      setSuccess(`Tax regime updated to ${newRegime} successfully.`);
      loadEmployeeTaxData();
    } catch (err) {
      setError(err.message || 'Failed to update tax regime.');
    } finally {
      setUpdatingRegime(false);
    }
  };


  const formatCurrency = (val) => 
    val !== null && val !== undefined ? `₹${parseFloat(val).toLocaleString('en-IN')}` : '₹0';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Banner */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Income Tax</h1>
          <p className="text-sm text-gray-500 mt-1">Review your tax regime selection and TDS projections.</p>
        </div>
        <div className="flex items-center gap-2 bg-gray-50 border rounded-lg p-2.5">
          <Calendar className="w-4 h-4 text-blue-600"/>
          <span className="text-sm font-semibold text-gray-700">Financial Year:</span>
          <select 
            value={taxYear} 
            onChange={(e) => setTaxYear(e.target.value)}
            className="bg-transparent text-sm font-bold text-gray-900 border-none outline-none cursor-pointer"
          >
            <option value="2026-27">2026-27</option>
            <option value="2027-28">2027-28</option>
          </select>
        </div>
      </div>

      {/* Nav Sub-Tabs */}
      <div className="flex border-b border-gray-200 pb-1 gap-2 overflow-x-auto">
        {[
          { id: 'summary', label: 'Tax Summary' },
          { id: 'calculation', label: 'Tax Calculation Details' },
          { id: 'history', label: 'TDS History' }
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveSubTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 whitespace-nowrap transition-colors ${
              activeSubTab === t.id ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Loading overlay / spinner */}
      {loading ? (
        <div className="py-16 text-center text-gray-500 font-medium bg-white rounded-xl border border-gray-100 shadow-sm">
          Loading tax records...
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          
          {/* TAB 1: SUMMARY */}
          {activeSubTab === 'summary' && (
            <div className="space-y-6">
              {/* Regime Selector Status Box */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Tax Regime Declaration
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Your current regime selection for FY {taxYear} is <strong className="text-blue-700">{profile?.regime || 'Not Decided'}</strong>.
                  </p>
                  <div className="mt-2.5 flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    Regime Status: <span className={`font-semibold capitalize ${profile?.status === 'LOCKED' ? 'text-red-600' : 'text-green-600'}`}>{profile?.status || 'PENDING'}</span>
                  </div>
                </div>

                {profile?.status === 'LOCKED' ? (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    Selection Locked for FY
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={updatingRegime || profile?.regime === 'NEW'}
                      onClick={() => handleRegimeChange('NEW')}
                      className={`px-4 py-2 text-sm font-bold rounded-lg border transition-colors ${
                        profile?.regime === 'NEW' 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      Declare NEW Regime
                    </button>
                    <button
                      type="button"
                      disabled={updatingRegime || profile?.regime === 'OLD'}
                      onClick={() => handleRegimeChange('OLD')}
                      className={`px-4 py-2 text-sm font-bold rounded-lg border transition-colors ${
                        profile?.regime === 'OLD' 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      Declare OLD Regime
                    </button>
                  </div>
                )}
              </div>

              {summary ? (
                <div className="space-y-6">
                  <h3 className="font-bold text-gray-900 text-lg">Annual Computation Projection</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-50 border p-4 rounded-xl">
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Annual Projected Gross</span>
                      <span className="text-xl font-extrabold text-gray-900 mt-1 block">{formatCurrency(summary.projected_annual_gross)}</span>
                    </div>
                    <div className="bg-gray-50 border p-4 rounded-xl">
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Standard Deduction</span>
                      <span className="text-xl font-extrabold text-gray-900 mt-1 block">{formatCurrency(summary.standard_deduction)}</span>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                      <span className="text-xs font-semibold text-blue-600 uppercase block">Taxable Income</span>
                      <span className="text-xl font-extrabold text-blue-900 mt-1 block">{formatCurrency(summary.taxable_income)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-50 border p-4 rounded-xl">
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Gross Annual Tax</span>
                      <span className="text-xl font-extrabold text-gray-900 mt-1 block">{formatCurrency(summary.annual_tax)}</span>
                    </div>
                    <div className="bg-gray-50 border p-4 rounded-xl">
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Education Cess (4%)</span>
                      <span className="text-xl font-extrabold text-gray-900 mt-1 block">{formatCurrency(summary.cess)}</span>
                    </div>
                    <div className="bg-gray-50 border p-4 rounded-xl">
                      <span className="text-xs font-semibold text-gray-500 uppercase block">Net Annual Tax Liability</span>
                      <span className="text-xl font-extrabold text-gray-900 mt-1 block">{formatCurrency(summary.final_annual_tax)}</span>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl">
                      <span className="text-xs font-semibold text-indigo-600 uppercase block">TDS Deducted YTD</span>
                      <span className="text-xl font-extrabold text-indigo-900 mt-1 block">{formatCurrency(summary.tds_deducted_ytd)}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 border p-5 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">Monthly Tax Deduction Forecast</h4>
                      <p className="text-xs text-gray-500 mt-0.5">Remaining Tax Liability divided by estimated future payroll runs.</p>
                    </div>
                    <div className="flex gap-4 items-center">
                      <div className="text-right">
                        <span className="text-xs font-semibold text-gray-400 uppercase block">Remaining Months</span>
                        <span className="text-sm font-bold text-gray-700 block">{summary.remaining_months} Months</span>
                      </div>
                      <div className="w-px h-8 bg-gray-300"></div>
                      <div className="text-right">
                        <span className="text-xs font-semibold text-indigo-600 uppercase block">Current Month TDS</span>
                        <span className="text-lg font-black text-indigo-900 block">{formatCurrency(summary.current_month_projected_tds)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 italic">No calculation worksheet available for the selected parameters.</div>
              )}
            </div>
          )}

          {/* TAB 2: TAX CALCULATION (WORKSHEET) */}
          {activeSubTab === 'calculation' && (
            <div className="space-y-6">
              <h3 className="font-bold text-gray-900 text-lg border-b pb-2">Progressive Slab Calculation Explanation</h3>
              {summary ? (
                <div className="space-y-6 text-sm">
                  {/* Slabs breakdown details if worksheet exists */}
                  <div className="overflow-x-auto border rounded-xl">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Calculation Worksheet Row</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase">Annualized Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 font-medium text-gray-900">
                        <tr>
                          <td className="px-4 py-2.5 text-gray-600">Projected Base Salary & Allowances (Actual + Projected Future)</td>
                          <td className="px-4 py-2.5 text-right">{formatCurrency(summary.projected_annual_gross)}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5 text-gray-600">Standard Deduction (Statutory Benefit)</td>
                          <td className="px-4 py-2.5 text-right text-green-700">-{formatCurrency(summary.standard_deduction)}</td>
                        </tr>
                        <tr className="bg-blue-50/50">
                          <td className="px-4 py-2.5 font-bold text-blue-900">Calculated Net Taxable Income</td>
                          <td className="px-4 py-2.5 text-right font-bold text-blue-900">{formatCurrency(summary.taxable_income)}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5 text-gray-600">Base Slab progressive tax</td>
                          <td className="px-4 py-2.5 text-right">{formatCurrency(summary.annual_tax)}</td>
                        </tr>
                        {summary.cess > 0 && (
                          <tr>
                            <td className="px-4 py-2.5 text-gray-600">Statutory Health & Education Cess (4%)</td>
                            <td className="px-4 py-2.5 text-right">{formatCurrency(summary.cess)}</td>
                          </tr>
                        )}
                        <tr className="bg-indigo-50/50">
                          <td className="px-4 py-2.5 font-bold text-indigo-900">Total Net Annual Tax Liability</td>
                          <td className="px-4 py-2.5 text-right font-bold text-indigo-900">{formatCurrency(summary.final_annual_tax)}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5 text-gray-600">TDS Already Deducted till Date (YTD)</td>
                          <td className="px-4 py-2.5 text-right text-indigo-700">-{formatCurrency(summary.tds_deducted_ytd)}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-4 py-2.5 font-bold text-gray-900">Remaining Annual Tax to Deduct</td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{formatCurrency(summary.remaining_tds)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-4 flex gap-3 text-xs text-blue-800">
                    <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Important Information:</p>
                      <p className="mt-1">
                        Slab calculation rules are generated dynamically from active policy setups configured by the HR Finance Governance Center.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 italic">Worksheet is not configured yet for the selected financial year.</div>
              )}
            </div>
          )}

          {/* TAB 3: TDS HISTORY */}
          {activeSubTab === 'history' && (
            <div className="space-y-4">
              <h3 className="font-bold text-gray-900 text-lg border-b pb-2">Finalized Monthly TDS Deductions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Payroll Month/Year</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Gross Income for Month</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">TDS Tax Deducted</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Cumulative YTD TDS</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tdsHistory.map((hist, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3.5 font-bold text-gray-900">{hist.payroll_month}/{hist.payroll_year}</td>
                        <td className="px-4 py-3.5 font-semibold text-gray-700">{formatCurrency(hist.current_taxable_income ?? ((hist.payroll_context?.basic || 0) + (hist.payroll_context?.hra || 0) + (hist.payroll_context?.da || 0)))}</td>
                        <td className="px-4 py-3.5 font-black text-indigo-900">{formatCurrency(hist.final_tds)}</td>
                        <td className="px-4 py-3.5 font-semibold text-gray-700">{formatCurrency(hist.tds_deducted_ytd ?? hist.tds_already_deducted)}</td>
                        <td className="px-4 py-3.5">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase bg-green-100 text-green-800">
                            Finalized
                          </span>
                        </td>
                      </tr>
                    ))}
                    {tdsHistory.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">No finalized TDS history found in this financial year.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default EmployeeIncomeTax;
